import { Pipe, PipeTransform } from '@angular/core';

/** Shown when a note has neither a Title nor any meaningful text content. */
export const UNTITLED_NOTE_LABEL = 'Untitled note';

/** Maximum length of a content-derived title (including the trailing ellipsis). */
export const DERIVED_TITLE_MAX_LENGTH = 80;

const BLOCK_BOUNDARY = /<br\s*\/?>|<\/(?:p|div|h[1-6]|li|blockquote|pre|tr|td|th|summary|details|section)\s*>/gi;
const NAMED_ENTITIES: Record<string, string> = { nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", '#39': "'" };

function hasTitle(title: string | null | undefined): boolean {
  return !!title && title.trim() !== '';
}

function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+|#39);/gi, (match, code: string) => {
    const lower = code.toLowerCase();
    if (lower in NAMED_ENTITIES) return NAMED_ENTITIES[lower];
    if (lower.startsWith('#x')) return String.fromCodePoint(parseInt(lower.slice(2), 16));
    if (lower.startsWith('#')) return String.fromCodePoint(parseInt(lower.slice(1), 10));
    return match;
  });
}

/** First non-blank text line of editor HTML, markup stripped and truncated with `…`. Empty string if none. */
export function extractFirstContentLine(html: string | null | undefined, maxLength = DERIVED_TITLE_MAX_LENGTH): string {
  if (!html) return '';
  const text = decodeEntities(
    html
      .replace(/<(script|style)[\s\S]*?<\/\1>/gi, '')
      .replace(BLOCK_BOUNDARY, '\n')
      .replace(/<[^>]*>/g, ''),
  );
  const line = text
    .split('\n')
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .find((l) => l !== '');
  if (!line) return '';
  return line.length > maxLength ? `${line.slice(0, maxLength - 1).trimEnd()}…` : line;
}

/**
 * Display-only title for a note: the Title if non-empty, else the first meaningful line of the
 * content, else "Untitled note". Never write the result back to `note.Title`.
 */
export function getNoteDisplayTitle(title: string | null | undefined, html: string | null | undefined): string {
  if (hasTitle(title)) return title!;
  return extractFirstContentLine(html) || UNTITLED_NOTE_LABEL;
}

/** True when the display title does not come from the note's own Title. */
export function isNoteDisplayTitleDerived(title: string | null | undefined): boolean {
  return !hasTitle(title);
}

/**
 * Template form of `getNoteDisplayTitle`: `{{ note.Title | noteDisplayTitle: note.DescriptionHtml }}`.
 * Pure on primitive inputs, so list rows only re-derive when the title or content actually changes.
 */
@Pipe({ name: 'noteDisplayTitle', standalone: true })
export class NoteDisplayTitlePipe implements PipeTransform {
  transform(title: string | null | undefined, html: string | null | undefined): string {
    return getNoteDisplayTitle(title, html);
  }
}
